import { Row, Col, Checkbox, Form as AntForm } from 'antd';
import { useSelector } from 'react-redux';
import DynamicCardForm from './DynamicCardForm';
import TabbedDynamicTable from './TabbedDynamicTable';
import EditableTextAreaModal from './EditableTextAreaModal';
import { TAMANO_LETRA_TABLAS_DEFAULT } from '../store/reducers/user';

const { Item: FormItem } = AntForm;

const actionButtonStyles = `
  .general-form-action-buttons-wrapper {
    border-top: 1px solid #f0f0f0;
    padding-top: 20px;
    margin-top: 20px;
  }

  .general-form-action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    justify-content: flex-end;
  }

  .general-form-action-buttons button,
  .general-form-action-buttons .ant-btn {
    width: 100% !important;
    height: 30px !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    border-radius: 0 !important;
    transition: all 0.22s ease !important;
    border: none !important;
    border-bottom-width: 3.5px !important;
    border-bottom-style: solid !important;
    padding-bottom: 3px !important;
  }

  .general-form-action-buttons button span,
  .general-form-action-buttons .ant-btn span {
    font-size: 12px !important;
    font-weight: 550 !important;
  }

  .general-form-action-buttons button:hover,
  .general-form-action-buttons .ant-btn:hover {
    transform: translateY(-1px) !important;
    border-radius: 6px !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
    border-bottom-width: 0.1px !important;
  }

  .general-form-action-buttons button:hover .anticon,
  .general-form-action-buttons .ant-btn:hover .anticon {
    color: #ffffff !important;
  }

  @media (max-width: 768px) {
    .general-form-action-buttons > * {
      flex: 1 1 auto;
      min-width: 140px;
      max-width: 100%;
    }
    .general-form-action-buttons {
      gap: 8px;
    }
  }

  @media (max-width: 1500px) {
    .ventas-obs-totales-row {
      flex-direction: column !important;
    }
    .ventas-obs-totales-row > .ant-col {
      max-width: 100% !important;
      min-width: auto !important;
      flex: 0 0 100% !important;
      width: 100% !important;
      margin-bottom: 12px;
    }
  }

  @media (max-width: 700px) { /* Ajustes para tablas de totales en móvil */
    .totales-table {
      display: block !important;
      width: 100%;
    }

    .totales-table tbody {
      display: block;
      width: 100%;
    }

    .totales-table tr {
      display: block;
      width: 100%;
      margin-bottom: 16px;
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      padding: 0;
    }

    .totales-table > tbody:first-of-type > tr:first-child {
      display: none;
    }

    .totales-table th,
    .totales-table td {
      display: block;
      width: 100% !important;
      padding: 8px 12px !important;
      border: none !important;
      text-align: left !important;
      margin-bottom: 0;
      box-sizing: border-box;
    }

    .totales-table th {
      font-weight: 600;
      background-color: #fafafa;
      border-bottom: 1px solid #f0f0f0 !important;
      margin-bottom: 8px;
    }

    .totales-table tbody tr td:nth-child(2):has(> *)::before { /* Solo si la celda tiene contenido, se muestra el label "Ajuste" */
      content: "Ajuste: ";
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #595959;
    }

    .totales-table tbody tr td:nth-child(3):not(:empty)::before { /* Solo si la celda no está vacía, se muestra el label "Valor" */
      content: "Valor: ";
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #595959;
    }

    .totales-table tbody tr td:nth-child(4)::before { 
      content: "XML: ";
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #595959;
    }

    .totales-table td {
      background-color: #ffffff;
      margin-bottom: 4px;
    }

    .totales-table .td-totales-right {
      text-align: left !important;
    }

    .totales-table .td-totales-center {
      text-align: left !important;
    }
  }

`;

const GeneralFormLayout = ({
  cards = [],
  tabsConfig,
  cardsGutter = [16, 16],
  observacionesConfig,
  actionButtons,
  isMobile = false,
  totalPagar = 0,
  totalesMaxWidth = '45%',
}) => {

  const hasObsContent = observacionesConfig && (
    observacionesConfig.observaciones?.value?.trim() ||
    observacionesConfig.observaciones2?.value?.trim()
  );
  const hasCamposFechas = observacionesConfig && Boolean(observacionesConfig.camposFechas);
  const hasCheckboxes = observacionesConfig && observacionesConfig.checkboxes?.length > 0;
  const hasObsCard = hasObsContent || hasCamposFechas || hasCheckboxes;

  const observacionesLength =
    (observacionesConfig?.observaciones?.value?.length || 0) +
    (observacionesConfig?.observaciones2?.value?.length || 0);
  const isObservacionesLarge = observacionesLength >= 120;

  // Tamaño de letra de la tabla. Se elige en Panel Principal > Configuración > General, por usuario.
  const tamanoLetraTablas = useSelector(
    (state) => state.user.tamanoLetraTablas || TAMANO_LETRA_TABLAS_DEFAULT
  );

  return (
    <div
      className="general-form-layout"
      style={{ padding: '0 16px', width: '100%', minWidth: 0, '--tabla-font-size': `${tamanoLetraTablas}px` }}
    >
      <style>{actionButtonStyles}</style>

      {/* Cards Section - instancia N DynamicCardForm */}
      {cards.length > 0 && (
        <Row gutter={cardsGutter} style={{ marginBottom: 16 }}>
          {cards.map((card, idx) => {
            if (card.hidden) return null;
            if (card.group) {
              const visibleSubCards = card.group.filter(subCard => !subCard.hidden);
              if (visibleSubCards.length === 0) return null;
              return (
                <Col
                  key={idx}
                  xs={card.xs ?? 24}
                  sm={card.sm ?? 24}
                  md={card.md ?? 24}
                  lg={card.lg ?? 24}
                  xl={card.xl ?? card.lg ?? 24}
                >
                  <Row gutter={[0, 16]}>
                    {visibleSubCards.map((subCard, subIdx) => (
                      <DynamicCardForm
                        key={subIdx}
                        title={subCard.title}
                        headerRight={subCard.headerRight}
                        xs={24}
                        sm={24}
                        md={24}
                        lg={24}
                        xl={24}
                      >
                        {subCard.content}
                      </DynamicCardForm>
                    ))}
                  </Row>
                </Col>
              );
            }
            return (
              <DynamicCardForm
                key={idx}
                title={card.title}
                headerRight={card.headerRight}
                xs={card.xs ?? 24}
                sm={card.sm ?? 24}
                md={card.md ?? 24}
                lg={card.lg ?? 24}
                xl={card.xl ?? card.lg ?? 24}
                marginTop={card.marginTop}
                style={card.style}
                bodyStyle={card.bodyStyle}
              >
                {card.content}
              </DynamicCardForm>
            );
          })}
        </Row>
      )}

      {/* TabbedDynamicTable abajo */}
      {tabsConfig && (
        <TabbedDynamicTable
          {...tabsConfig}
          tableClassName={`${tabsConfig.tableClassName || 'tabla-insumos-venta'} tabla-compacta`}
        />
      )}

      {/* Sección de Observaciones y Totales */}
      {observacionesConfig && (
        <Row
          gutter={24}
          className="ventas-obs-totales-row"
          style={{ marginTop: '3.125rem', width: '100%', minWidth: 0 }}
          justify={isMobile ? 'start' : 'end'}
          align="top"
        >
          {/* Card Observaciones (solo si hay contenido) */}
          {hasObsCard && (
            <Col
              xs={24}
              sm={24}
              md={isMobile ? 24 : 'auto'}
              lg={isMobile ? 24 : 'auto'}
              flex={isMobile ? undefined : '0 1 auto'}
              style={{
                maxWidth: isMobile ? '100%' : (hasObsContent ? '40%' : 'auto'),
                transition: 'max-width 0.35s ease, transform 0.35s ease',
                marginBottom: isMobile ? 12 : 0,
              }}
            >
              <DynamicCardForm span={24} noMargin>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    flexDirection: isMobile ? 'column' : 'row',
                    flexWrap: isMobile ? 'nowrap' : 'wrap',
                    transition: 'all 0.35s ease',
                  }}
                >
                  {/* Observaciones */}
                  <div style={{ minWidth: isMobile ? '100%' : 200, width: isMobile ? '100%' : 'auto' }}>
                    {observacionesConfig.observaciones && (
                      <EditableTextAreaModal
                        label="Observaciones"
                        value={observacionesConfig.observaciones.value}
                        onSave={observacionesConfig.observaciones.onSave}
                        disabled={observacionesConfig.observaciones.disabled}
                        rows={4}
                      />
                    )}
                    {observacionesConfig.observaciones2 && (
                      <EditableTextAreaModal
                        label="Observaciones Internas"
                        value={observacionesConfig.observaciones2.value}
                        tooltip="Estas observaciones no se imprimirán en la venta."
                        onSave={observacionesConfig.observaciones2.onSave}
                        disabled={observacionesConfig.observaciones2.disabled}
                        rows={4}
                      />
                    )}
                  </div>

                  {/* Checkboxes */}
                  {hasCheckboxes && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : (isObservacionesLarge ? 'row' : 'column'),
                        gap: isMobile ? 8 : (isObservacionesLarge ? 16 : 8),
                        alignItems: isMobile ? 'flex-start' : (isObservacionesLarge ? 'center' : 'flex-start'),
                        minWidth: isMobile ? '100%' : (isObservacionesLarge ? 'auto' : 180),
                        width: isMobile ? '100%' : 'auto',
                        marginTop: isMobile ? 12 : (!isObservacionesLarge ? 12 : 0),
                        transition: 'all 0.25s ease',
                      }}
                    >
                      {observacionesConfig.checkboxes.map((checkbox, index) => (
                        <FormItem
                          key={index}
                          name={checkbox.name}
                          valuePropName="checked"
                          initialValue={checkbox.initialValue}
                          style={{ marginBottom: 0, whiteSpace: 'nowrap' }}
                        >
                          <Checkbox onChange={checkbox.onChange} disabled={checkbox.disabled}>
                            <span style={{ marginLeft: 6, fontSize: 12 }}>
                              {checkbox.label}
                            </span>
                          </Checkbox>
                        </FormItem>
                      ))}
                    </div>
                  )}

                  {/* Fechas */}
                  {hasCamposFechas && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : (isObservacionesLarge ? 'row' : 'column'),
                        gap: isMobile ? 8 : (isObservacionesLarge ? 16 : 8),
                        alignItems: isMobile ? 'flex-start' : (isObservacionesLarge ? 'center' : 'flex-start'),
                        minWidth: isMobile ? '100%' : (isObservacionesLarge ? 'auto' : 180),
                        width: isMobile ? '100%' : 'auto',
                        marginTop: isMobile ? 12 : (!isObservacionesLarge ? 12 : 0),
                        transition: 'all 0.25s ease',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {observacionesConfig.camposFechas}
                    </div>
                  )}
                </div>
              </DynamicCardForm>
            </Col>
          )}

          {/* Card Totales Orden de Compra */}
          {observacionesConfig.totalesOrdenCompra && (
            <Col
              xs={24}
              sm={24}
              md={isMobile ? 24 : 'auto'}
              lg={isMobile ? 24 : 'auto'}
              flex={isMobile ? undefined : '1 1 auto'}
              style={{ minWidth: isMobile ? '100%' : 'auto', maxWidth: isMobile ? '100%' : '35%', marginLeft: isMobile ? undefined : (hasObsCard ? undefined : 'auto') }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  borderLeft: '4px solid #FF6A0D',
                  padding: '8px',
                  width: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                }}
              >
                {observacionesConfig.totalesOrdenCompra}
              </div>
            </Col>
          )}

          {/* Card Totales Factura */}
          {observacionesConfig.totalesFactura && (
            <Col
              xs={24}
              sm={24}
              md={isMobile ? 24 : 'auto'}
              lg={isMobile ? 24 : 'auto'}
              flex={isMobile ? undefined : '1 1 auto'}
              style={{ minWidth: isMobile ? '100%' : 'auto', maxWidth: isMobile ? '100%' : (observacionesConfig.totalesFacturaMaxWidth || '35%'), marginLeft: isMobile ? undefined : (hasObsCard || observacionesConfig.totalesOrdenCompra ? undefined : 'auto') }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  borderLeft: '4px solid #00BFD6',
                  padding: '8px',
                  width: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                }}
              >
                {observacionesConfig.totalesFactura}
              </div>
            </Col>
          )}

          {/* Compatibilidad: Card Totales (formato antiguo) */}
          {!observacionesConfig.totalesOrdenCompra && !observacionesConfig.totalesFactura && observacionesConfig.totales && (
            <Col
              xs={24}
              sm={24}
              md={isMobile ? 24 : 'auto'}
              lg={isMobile ? 24 : 'auto'}
              flex={isMobile ? undefined : '1 1 auto'}
              style={{ minWidth: isMobile ? '100%' : 'auto', maxWidth: isMobile ? '100%' : totalesMaxWidth, marginLeft: isMobile ? undefined : (hasObsCard ? undefined : 'auto') }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  borderLeft: '4px solid #FF6A0D',
                  padding: '8px',
                  width: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                }}
              >
                {observacionesConfig.totales}
              </div>
            </Col>
          )}
        </Row>
      )}

      {/* Botones de Acción */}
      {actionButtons !== undefined && actionButtons !== null && (
        <div className="general-form-action-buttons-wrapper">
          <div className="general-form-action-buttons">
            {actionButtons}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralFormLayout;